(() => {
    const SIZE_CHART_HASH = "size-chart";
    const PRODUCT_PAGE = "/pages/product";
    const SIZE_LABELS = new Set([
        "size chart",
        "size charts",
        "product size chart",
        "product size charts",
    ]);

    const SKIP_ROW =
        ".text-block ul a, .text-block ul li, .text-block ol a, .text-block ol li, rte-formatter ul a, rte-formatter ul li, rte-formatter ol a, rte-formatter ol li";
    const SKIP_LINK =
        ".text-block ul a, .text-block ol a, rte-formatter ul a, rte-formatter ol a";

    const ALIASES = {
        "size charts": [
            "product size charts",
            "product size chart",
            "size chart",
        ],
        "size chart": [
            "product size charts",
            "product size chart",
            "size charts",
        ],
        "product material, styles, & care": [
            "product material, styles, and care",
            "product material, styles & care",
            "product material styles & care",
        ],
    };

    const normalize = (value) =>
        (value || "").replace(/\s+/g, " ").trim().toLowerCase();

    const looseLabel = (value) =>
        normalize(value)
            .replace(/&/g, " and ")
            .replace(/[^a-z0-9]+/g, " ")
            .replace(/\s+/g, " ")
            .trim();

    const slugify = (value) =>
        normalize(value)
            .replace(/&/g, " and ")
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "");

    const headerOffset = () => {
        const header =
            document.querySelector("sticky-header") ||
            document.querySelector("#header-group") ||
            document.querySelector("header-component") ||
            document.querySelector("header");
        return (header?.getBoundingClientRect().height || 0) + 16;
    };

    const openAccordions = (el) => {
        let details = el.closest?.("details");
        while (details) {
            if (!details.open) details.open = true;
            details = details.parentElement
                ? details.parentElement.closest("details")
                : null;
        }
    };

    const scrollTo = (el) => {
        if (!el) return;
        openAccordions(el);
        const top =
            el.getBoundingClientRect().top + window.scrollY - headerOffset();
        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    };

    const isSkipListLink = (link) => {
        if (!(link instanceof HTMLAnchorElement)) return false;
        if (link.closest(".side-nav-block")) return false;

        const block = link.closest(".text-block, rte-formatter");
        if (!block) return false;

        const list = link.closest("ul, ol");
        return Boolean(list && block.contains(list));
    };

    const isSizeChartLink = (link) => {
        if (!(link instanceof HTMLAnchorElement)) return false;
        return SIZE_LABELS.has(looseLabel(link.textContent));
    };

    const isSizeChartNav = (link) => {
        if (!(link instanceof HTMLAnchorElement)) return false;
        if (!link.classList.contains("side-nav__link")) return false;
        return SIZE_LABELS.has(
            looseLabel(
                link.querySelector(".side-nav__label")?.textContent ||
                    link.textContent,
            ),
        );
    };

    const isProductPage = () =>
        location.pathname.replace(/\/$/, "") ===
        getProductPath().replace(/\/$/, "");

    const getSizeChart = () =>
        document.getElementById(SIZE_CHART_HASH) ||
        document.querySelector(".size-chart");

    const isExcludedTarget = (el) => {
        if (!el) return true;
        if (el.closest(".side-nav-block")) return true;

        const list = el.closest("ul, ol");
        if (list?.querySelector("a")) return true;

        return false;
    };

    const labelMatches = (text, labels) => {
        const loose = looseLabel(text);
        if (!loose) return false;

        return labels.some((label) => {
            const target = looseLabel(label);
            return (
                loose === target ||
                loose.startsWith(target) ||
                target.startsWith(loose) ||
                loose.includes(target) ||
                target.includes(loose)
            );
        });
    };

    const ensureId = (el, fallbackLabel) => {
        if (el.id) return el.id;

        const base = slugify(fallbackLabel || el.textContent || "section");
        let id = base || "section";
        let i = 2;
        while (document.getElementById(id)) {
            id = `${base}-${i}`;
            i += 1;
        }
        el.id = id;
        return id;
    };

    const scrollTargetFor = (el, label, sizeChart) => {
        const scrollEl =
            el.closest("details") ||
            el.closest(".accordion") ||
            el.closest(".text-block") ||
            el;

        return {
            id: ensureId(scrollEl, label),
            el: scrollEl,
            sizeChart,
        };
    };

    const findTarget = (linkLabel) => {
        const label = normalize(linkLabel);
        if (!label) return null;

        const labels = [label, ...(ALIASES[label] || [])];

        if (labels.some((entry) => SIZE_LABELS.has(looseLabel(entry)))) {
            const chart = getSizeChart();
            if (chart) {
                if (!chart.id) chart.id = SIZE_CHART_HASH;
                return scrollTargetFor(chart, label, true);
            }
        }

        const selectors = [
            `#${SIZE_CHART_HASH}`,
            ".size-chart",
            ".details__header",
            "h1",
            "h2",
            "h3",
            "h4",
            "h5",
            "h6",
            ".text-block.h1",
            ".text-block.h2",
            ".text-block.h3",
            ".text-block.h4",
            ".text-block.h5",
            ".text-block.h6",
            ".text-block p",
            ".text-block strong",
            "rte-formatter p",
            "rte-formatter strong",
        ].join(", ");

        for (const el of document.querySelectorAll(selectors)) {
            if (isExcludedTarget(el)) continue;
            if (!labelMatches(el.textContent || "", labels)) continue;
            return scrollTargetFor(el, label, false);
        }

        return null;
    };

    const goTo = (target) => {
        if (!target) return;
        history.pushState(null, "", `#${target.id}`);
        const scroll = () => scrollTo(target.el);
        if (target.sizeChart) {
            requestAnimationFrame(() => setTimeout(scroll, 50));
        } else {
            scroll();
        }
    };

    const getProductPath = () => {
        const productLink = [...document.querySelectorAll(".side-nav__link")].find(
            (link) =>
                looseLabel(
                    link.querySelector(".side-nav__label")?.textContent ||
                        link.textContent,
                ) === "product",
        );
        return productLink
            ? new URL(productLink.href, location.origin).pathname
            : PRODUCT_PAGE;
    };

    const resolveFromHref = (link) => {
        const href = link.getAttribute("href") || "";
        if (!href || href === "#") return null;

        try {
            const url = new URL(href, location.origin);
            if (url.origin !== location.origin) return null;
            if (
                url.pathname.replace(/\/$/, "") !==
                location.pathname.replace(/\/$/, "")
            ) {
                return null;
            }

            const hash = url.hash.replace("#", "");
            if (!hash) return null;

            const el =
                document.getElementById(hash) ||
                (hash === SIZE_CHART_HASH ? getSizeChart() : null);
            if (!el) return null;

            return scrollTargetFor(
                el,
                link.textContent || "",
                hash === SIZE_CHART_HASH,
            );
        } catch {
            return null;
        }
    };

    const resolveLinkTarget = (link) =>
        findTarget(link.textContent || "") || resolveFromHref(link);

    const wireSkipLinks = () => {
        document.querySelectorAll(SKIP_LINK).forEach((link) => {
            if (!isSkipListLink(link)) return;
            const target = resolveLinkTarget(link);
            if (!target) return;
            link.setAttribute("href", `#${target.id}`);
            link.removeAttribute("target");
        });
    };

    const wireSizeChartNav = () => {
        const chart = getSizeChart();
        if (chart && !chart.id) chart.id = SIZE_CHART_HASH;

        const productPath = getProductPath();

        document.querySelectorAll(".side-nav__link").forEach((link) => {
            if (!isSizeChartNav(link)) return;
            link.setAttribute(
                "href",
                chart && isProductPage()
                    ? `#${SIZE_CHART_HASH}`
                    : `${productPath}#${SIZE_CHART_HASH}`,
            );
            link.removeAttribute("target");
        });
    };

    const wireFooterSizeChartLinks = () => {
        const chart = getSizeChart();
        if (chart && !chart.id) chart.id = SIZE_CHART_HASH;

        const productPath = getProductPath();

        document
            .querySelectorAll(
                ".footer-content a, footer a, .shopify-section-group-footer-group a",
            )
            .forEach((link) => {
                if (!isSizeChartLink(link) || link.closest(".side-nav-block"))
                    return;
                link.setAttribute(
                    "href",
                    chart && isProductPage()
                        ? `#${SIZE_CHART_HASH}`
                        : `${productPath}#${SIZE_CHART_HASH}`,
                );
                link.removeAttribute("target");
            });
    };

    const handleHash = () => {
        const hash = location.hash.replace("#", "");
        if (!hash) return;

        if (hash === SIZE_CHART_HASH && getSizeChart()) {
            goTo(scrollTargetFor(getSizeChart(), "size charts", true));
            return;
        }

        const el = document.getElementById(hash);
        if (el) scrollTo(el.closest("details") || el.closest(".accordion") || el);
    };

    const init = () => {
        wireFooterSizeChartLinks();
        if (document.querySelector(".side-nav-block")) {
            wireSkipLinks();
            wireSizeChartNav();
        }
    };

    document.addEventListener(
        "click",
        (event) => {
            if (event.defaultPrevented) return;
            if (event.button !== 0) return;
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
                return;

            if (event.target.closest?.(".size-chart")) return;

            const skipHost = event.target.closest?.(SKIP_ROW);
            if (skipHost) {
                const link =
                    skipHost.closest("a") || skipHost.querySelector("a");
                if (link && isSkipListLink(link)) {
                    const target = resolveLinkTarget(link);
                    if (target) {
                        event.preventDefault();
                        event.stopImmediatePropagation();
                        goTo(target);
                        return;
                    }
                }
            }

            const navLink = event.target.closest?.("a.side-nav__link");
            if (navLink && isSizeChartNav(navLink)) {
                if (getSizeChart() && isProductPage()) {
                    event.preventDefault();
                    event.stopPropagation();
                    goTo(scrollTargetFor(getSizeChart(), "size charts", true));
                    return;
                }

                event.preventDefault();
                event.stopPropagation();
                location.assign(`${getProductPath()}#${SIZE_CHART_HASH}`);
                return;
            }

            const footerLink = event.target.closest?.("a");
            if (
                footerLink &&
                isSizeChartLink(footerLink) &&
                !footerLink.closest(".side-nav-block") &&
                getSizeChart() &&
                isProductPage()
            ) {
                event.preventDefault();
                event.stopPropagation();
                goTo(scrollTargetFor(getSizeChart(), "size charts", true));
            }
        },
        true,
    );

    const boot = () => {
        init();
        handleHash();
    };

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", boot);
    } else {
        boot();
    }

    window.addEventListener("load", init);
    window.addEventListener("hashchange", handleHash);
    document.addEventListener("shopify:section:load", init);
})();
